import React from 'react';
import {
  Card,
  Typography,
  Result,
  Button,
  Space,
  Progress,
  Tag,
  Statistic,
  Row,
  Col
} from 'antd';
import {
  TrophyOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

interface TestResultData {
  recordId: string;
  score: {
    correct: number;
    total: number;
    percentage: number;
  };
  timeSpent: number;
  gradedAnswers: Array<{
    questionId: string;
    answer: string;
    isCorrect: boolean;
    timeSpent: number;
  }>;
  passed: boolean;
}

interface TestResultProps {
  result: TestResultData;
  testTitle: string;
  onBackToDashboard: () => void;
}

const TestResult: React.FC<TestResultProps> = ({ result, testTitle, onBackToDashboard }) => {
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return '#52c41a'; // green
    if (percentage >= 60) return '#faad14'; // yellow
    return '#ff4d4f'; // red
  };

  const getResultStatus = () => {
    if (result.passed) {
      return {
        status: 'success' as const,
        title: 'Test Completed Successfully!',
        subTitle: `You passed with ${result.score.percentage}%`,
        icon: <TrophyOutlined style={{ color: '#52c41a' }} />
      };
    } else {
      return {
        status: 'warning' as const,
        title: 'Test Completed',
        subTitle: `Score: ${result.score.percentage}% (Passing grade: 60%)`,
        icon: <CloseCircleOutlined style={{ color: '#faad14' }} />
      };
    }
  };

  const resultConfig = getResultStatus();

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <Card>
        <Result
          status={resultConfig.status}
          title={resultConfig.title}
          subTitle={
            <Space direction="vertical" align="center">
              <Text>{resultConfig.subTitle}</Text>
              <Text type="secondary">{testTitle}</Text>
            </Space>
          }
          extra={[
            <Button type="primary" onClick={onBackToDashboard} key="dashboard">
              Back to Dashboard
            </Button>
          ]}
        />
      </Card>

      <Card style={{ marginTop: '20px' }} title="Test Results">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Score"
              value={result.score.percentage}
              suffix="%"
              valueStyle={{ color: getScoreColor(result.score.percentage) }}
              prefix={result.passed ? <TrophyOutlined /> : undefined}
            />
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Correct Answers"
              value={result.score.correct}
              suffix={`/ ${result.score.total}`}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Time Spent"
              value={formatTime(result.timeSpent)}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>Status</div>
              <Tag 
                color={result.passed ? 'green' : 'orange'} 
                style={{ fontSize: '14px', padding: '4px 12px' }}
              >
                {result.passed ? 'PASSED' : 'NEEDS IMPROVEMENT'}
              </Tag>
            </div>
          </Col>
        </Row>

        <div style={{ marginTop: '24px' }}>
          <Title level={5}>Performance Breakdown</Title>
          <Progress
            percent={result.score.percentage}
            strokeColor={getScoreColor(result.score.percentage)}
            strokeWidth={12}
            format={percent => `${percent}% (${result.score.correct}/${result.score.total})`}
          />
        </div>

        {result.gradedAnswers && result.gradedAnswers.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <Title level={5}>Question Summary</Title>
            <Space wrap>
              {result.gradedAnswers.map((answer, index) => (
                <Tag
                  key={index}
                  color={answer.isCorrect ? 'green' : 'red'}
                  style={{ margin: '4px' }}
                >
                  Q{index + 1}: {answer.isCorrect ? 'Correct' : 'Incorrect'}
                </Tag>
              ))}
            </Space>
          </div>
        )}
      </Card>
    </div>
  );
};

export default TestResult;